import React from "react";
import { AlertTriangle, CheckCircle2, Wallet, CalendarCheck, CalendarX, Star, Repeat, RotateCcw, MessageSquare, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// Ordered by importance (most important first) — drives block icons, the detail window and the legend.
export const FLAGS = [
  { key: "alert", label: "Medical alert", desc: "Must be acknowledged before treatment starts", keep: true, test: (b) => b.client_has_alerts,
    icon: (cls) => <AlertTriangle className={`${cls} text-amber-500 fill-amber-300/40`} />, chip: "bg-red-50 text-red-700 border-red-200" },
  { key: "deposit-unpaid", label: "Deposit unpaid", desc: "Online booking still waiting on its deposit", keep: true, test: (b) => !b.paid && !b.deposit_paid && b.deposit_pay_later,
    icon: () => <span className="text-[8px] font-bold uppercase tracking-wide rounded-sm px-1 bg-amber-400 text-slate-900 leading-[12px]">Dep</span>, chip: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "owing", label: "Awaiting payment", desc: "Checked out — payment link sent, money not received yet", keep: true, test: (b) => !b.paid && b.payment_status === "owing",
    icon: () => <span className="text-[8px] font-bold uppercase tracking-wide rounded-sm px-1 bg-amber-400 text-slate-900 leading-[12px]">Owing</span>, chip: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "paid", label: "Paid", desc: "Checked out and paid in full", keep: true, test: (b) => b.paid,
    icon: (cls) => <CheckCircle2 className={`${cls} text-emerald-500`} />, chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "deposit", label: "Deposit paid", desc: "Deposit received — balance due at checkout", keep: true, test: (b) => !b.paid && b.deposit_paid,
    icon: (cls) => <Wallet className={`${cls} text-emerald-500`} />, chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "future", label: "Has a future booking", desc: "Client is already booked in again — no icon means follow up to rebook", keep: true, test: (b) => b.has_future,
    icon: (cls) => <span className="inline-flex items-center justify-center h-[15px] w-[15px] rounded-full bg-emerald-500 text-white shrink-0 shadow-sm"><CalendarCheck className="h-2.5 w-2.5" strokeWidth={3} /></span>, chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "new", label: "New client", desc: "First visit to the clinic", test: (b) => b.is_new_client,
    icon: (cls) => <Star className={`${cls} fill-amber-400 text-amber-400`} />, chip: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "series", label: "Recurring series", desc: "Part of a repeating set of appointments", test: (b) => !!b.series_id,
    icon: (cls) => <Repeat className={cls} />, chip: "bg-muted text-foreground border-border" },
  { key: "rebook", label: "Rebooked at checkout", desc: "Made while the client was checking out", test: (b) => b.source === "rebook",
    icon: (cls) => <RotateCcw className={cls} />, chip: "bg-muted text-foreground border-border" },
  { key: "sms", label: "Text sent", desc: "Confirmation or reminder SMS has gone out", test: (b) => b.reminder_sent || b.confirmation_sent,
    icon: (cls) => <MessageSquare className={cls} />, chip: "bg-muted text-foreground border-border" },
];

const COLOURS = [
  ["Block colour", "Service or staff colour, with a solid bar on the left"],
  ["Faded / grey", "Cancelled appointment"],
  ["Hatched strip", "Buffer / clean-up time after the service"],
  ["Striped grey", "Outside opening hours or closed"],
];

const TID = { alert: "alert", "deposit-unpaid": "deposit-unpaid", paid: "paid", deposit: "deposit", future: "future", new: "new", series: "series", rebook: "rebook", sms: "sms" };

export const BookingIcons = ({ b, isMobile }) => (
  <>
    {FLAGS.filter((f) => f.test(b)).map((f) => (
      <span key={f.key} title={f.label} data-keep={f.keep ? "1" : undefined} className={`inline-flex items-center ${isMobile && !f.keep ? "hidden" : ""}`} data-testid={`icon-${TID[f.key]}-${b.id}`}>{f.icon("h-2.5 w-2.5")}</span>
    ))}
  </>
);

export const DetailFlags = ({ b }) => {
  const on = FLAGS.filter((f) => f.test(b));
  const followUp = b.client_id && b.status !== "cancelled" && !b.has_future;
  if (!on.length && !followUp) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="detail-flags">
      {on.map((f) => <span key={f.key} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${f.chip}`} data-testid={`detail-flag-${f.key}`}>{f.icon("h-3 w-3")}{f.label}</span>)}
      {followUp && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-[11px] font-medium" data-testid="detail-future-no"><CalendarX className="h-3 w-3" />No future booking — follow up</span>}
    </div>
  );
};

const LegendBody = () => (
  <>
    <ol className="px-4 py-2 divide-y">
      {FLAGS.map((f, i) => (
        <li key={f.key} className="flex items-start gap-3 py-2" data-testid={`legend-${f.key}`}>
          <span className="text-[10px] font-mono text-muted-foreground w-4 pt-0.5">{i + 1}</span>
          <span className="inline-flex items-center justify-center h-5 w-5 shrink-0 text-foreground">{f.icon("h-3.5 w-3.5")}</span>
          <div className="min-w-0"><div className="text-sm font-medium leading-tight">{f.label}</div><div className="text-[11px] text-muted-foreground leading-snug">{f.desc}</div></div>
        </li>
      ))}
    </ol>
    <div className="px-4 py-3 border-t bg-muted/40">
      <div className="font-mono-label text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Colours</div>
      {COLOURS.map(([l, d]) => <div key={l} className="text-[11px] leading-snug"><span className="font-medium">{l}</span> — <span className="text-muted-foreground">{d}</span></div>)}
    </div>
  </>
);

export const CalendarLegend = ({ trigger }) => (
  <Popover>
    <PopoverTrigger asChild>{trigger || <Button variant="outline" size="icon" className="rounded-lg" title="Icon key" data-testid="cal-legend-button"><Info className="h-4 w-4" /></Button>}</PopoverTrigger>
    <PopoverContent align="end" className="w-80 p-0" data-testid="cal-legend">
      <div className="px-4 py-3 border-b"><div className="font-heading font-semibold text-sm">Calendar key</div><div className="text-[11px] text-muted-foreground">Icons in order of importance — the most important shows first on a block.</div></div>
      <LegendBody />
    </PopoverContent>
  </Popover>
);

export const CalendarLegendDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm p-0 overflow-hidden max-h-[85vh] overflow-y-auto" data-testid="cal-legend-m">
      <DialogHeader className="px-4 pt-4 pb-3 border-b"><DialogTitle className="font-heading text-base">Calendar key</DialogTitle><p className="text-[11px] text-muted-foreground">Icons in order of importance — the most important shows first on a block.</p></DialogHeader>
      <LegendBody />
    </DialogContent>
  </Dialog>
);
